import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

const DEFAULT_VAPID_KEY = 'BCkUEhh0HjJrl-9xHrmK1mkhjjF82LXqyY_v1lKswvODM7qBGjvT-ORsomNUw3NL94jU9dpaQjSZXqoQkC3o9zo';
const STORAGE_KEY = 'digestlog_notifications_enabled';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isIOS(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    ('standalone' in window.navigator && (window.navigator as any).standalone === true) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

export function getReminderContent(slotId: string, label: string) {
  switch (slotId) {
    case 'morning':
      return {
        title: 'Morning Gut Check-In',
        body: 'How does your stomach feel upon waking? Log your morning comfort score and Bristol stool type.',
        url: '/?action=morning',
      };
    case 'breakfast':
      return {
        title: 'Breakfast Meal Log',
        body: 'Remember to log your breakfast and ingredients to keep your gut correlation timeline accurate.',
        url: '/?action=log&type=meal',
      };
    case 'midmorning':
      return {
        title: 'Midday Feelings & Energy Check',
        body: 'How is your stomach and energy feeling mid-morning? Record any early bloating or reflux sensations.',
        url: '/?action=log&type=symptom',
      };
    case 'lunch':
      return {
        title: 'Lunch Meal Log',
        body: 'Time for your midday meal! Snap a quick photo or tag your meal ingredients.',
        url: '/?action=log&type=meal',
      };
    case 'afternoon':
      return {
        title: 'Afternoon Digestion Check',
        body: 'How is your digestion feeling post-lunch? Log any distension, cramps, or comfort ratings.',
        url: '/?action=log&type=symptom',
      };
    case 'dinner':
      return {
        title: 'Dinner Meal Log',
        body: 'Remember to log your evening meal to monitor your next-morning digestive response.',
        url: '/?action=log&type=meal',
      };
    case 'evening':
      return {
        title: 'Evening Reflection & Feelings Check',
        body: 'How is your stomach feeling after dinner? Record your evening comfort score.',
        url: '/?action=log&type=symptom',
      };
    default:
      return {
        title: `DigestLog: ${label}`,
        body: 'Time for your scheduled digestive check-in.',
        url: '/',
      };
  }
}

async function scheduleNativeCheckinReminders() {
  try {
    await LocalNotifications.cancel({ notifications: [{ id: 101 }, { id: 102 }, { id: 103 }, { id: 104 }] });
    await LocalNotifications.schedule({
      notifications: [
        {
          id: 101,
          title: 'Morning Gut Check-In',
          body: 'How does your stomach feel upon waking? Log your morning comfort score.',
          schedule: { on: { hour: 8, minute: 30 }, repeats: true },
        },
        {
          id: 102,
          title: 'Lunch Meal Log',
          body: 'Time for your midday meal! Snap a quick photo or record your ingredients.',
          schedule: { on: { hour: 12, minute: 30 }, repeats: true },
        },
        {
          id: 103,
          title: 'Afternoon Digestion Check',
          body: 'How is your digestion feeling post-lunch? Log any distension or bloating.',
          schedule: { on: { hour: 15, minute: 0 }, repeats: true },
        },
        {
          id: 104,
          title: 'Evening Digestive Log',
          body: 'Remember to log your evening meal and bedtime comfort ratings.',
          schedule: { on: { hour: 20, minute: 0 }, repeats: true },
        },
      ],
    });
  } catch (e) {
    console.warn('Failed to schedule native reminders:', e);
  }
}

export function usePush() {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    }
    return false;
  });
  const [loading, setLoading] = useState(false);

  const isIOSDevice = isIOS();
  const isStandaloneMode = isStandalone();

  const syncSubscriptionWithServer = async (sub: PushSubscription) => {
    try {
      const subJSON = sub.toJSON ? sub.toJSON() : { endpoint: sub.endpoint };
      const scheduleRaw = localStorage.getItem('digestlog_reminder_schedule');
      const schedule = scheduleRaw ? JSON.parse(scheduleRaw) : undefined;
      await fetch('/api/push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: subJSON,
          schedule,
          timezoneOffset: new Date().getTimezoneOffset(),
        }),
      });
    } catch (err) {
      console.warn('Could not sync push subscription to server:', err);
    }
  };

  const checkSubscription = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator) || !('Notification' in window)) {
        return;
      }

      setPermission(Notification.permission);
      const userWantsPush = localStorage.getItem(STORAGE_KEY) === 'true';

      if (Notification.permission === 'denied') {
        setIsSubscribed(false);
        localStorage.setItem(STORAGE_KEY, 'false');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      if (!reg.pushManager) {
        setIsSubscribed(userWantsPush && Notification.permission === 'granted');
        return;
      }

      const sub = await reg.pushManager.getSubscription();

      if (sub) {
        setIsSubscribed(true);
        localStorage.setItem(STORAGE_KEY, 'true');
        // Ensure backend has current subscription & schedule
        syncSubscriptionWithServer(sub).catch(() => {});
      } else if (userWantsPush && Notification.permission === 'granted') {
        try {
          const newSub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(DEFAULT_VAPID_KEY),
          });
          if (newSub) {
            setIsSubscribed(true);
            localStorage.setItem(STORAGE_KEY, 'true');
            await syncSubscriptionWithServer(newSub);
          }
        } catch (subErr) {
          console.warn('Silent push resubscription skipped:', subErr);
          setIsSubscribed(true);
        }
      } else {
        setIsSubscribed(false);
      }
    } catch (e) {
      console.warn('Failed to check push subscription', e);
    }
  }, []);

  // Check for scheduled reminders every 30s while app is open or active
  useEffect(() => {
    if (!isSubscribed || permission !== 'granted') return;

    const checkReminders = async () => {
      try {
        const scheduleRaw = localStorage.getItem('digestlog_reminder_schedule');
        if (!scheduleRaw) return;
        const slots: Array<{ id: string; label: string; time: string; enabled: boolean }> = JSON.parse(scheduleRaw);

        const now = new Date();
        const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();
        const todayKey = now.toISOString().slice(0, 10);

        for (const slot of slots) {
          if (!slot.enabled || !slot.time) continue;

          const [slotH, slotM] = slot.time.split(':').map(Number);
          if (isNaN(slotH) || isNaN(slotM)) continue;

          const slotTotalMinutes = slotH * 60 + slotM;
          const diffMinutes = currentTotalMinutes - slotTotalMinutes;

          // Trigger if reached slot time today within a 45-minute window and not yet fired today
          if (diffMinutes >= 0 && diffMinutes <= 45) {
            const firedKey = `digestlog_fired_${slot.id}_${todayKey}`;
            if (!localStorage.getItem(firedKey)) {
              localStorage.setItem(firedKey, 'true');

              const content = getReminderContent(slot.id, slot.label);

              if ('serviceWorker' in navigator) {
                const reg = await navigator.serviceWorker.ready;
                await reg.showNotification(content.title, {
                  body: content.body,
                  icon: '/icons/icon-192.png',
                  badge: '/favicon.svg',
                  tag: `digestlog-slot-${slot.id}`,
                  data: { url: content.url },
                });
              } else if ('Notification' in window && Notification.permission === 'granted') {
                new Notification(content.title, {
                  body: content.body,
                  icon: '/icons/icon-192.png',
                });
              }
            }
          }
        }
      } catch (err) {
        console.warn('Reminder ticker error:', err);
      }
    };

    const interval = setInterval(checkReminders, 30000);
    checkReminders();
    return () => clearInterval(interval);
  }, [isSubscribed, permission]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      setIsSupported(true);
      LocalNotifications.checkPermissions()
        .then((perm) => {
          const isGranted = perm.display === 'granted';
          setPermission(isGranted ? 'granted' : perm.display === 'denied' ? 'denied' : 'default');
          const userWantsPush = localStorage.getItem(STORAGE_KEY) === 'true';
          setIsSubscribed(userWantsPush && isGranted);
        })
        .catch(() => {});
      return;
    }

    if (typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator) {
      setIsSupported(true);
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, [checkSubscription]);

  const subscribe = async () => {
    if (Capacitor.isNativePlatform()) {
      setLoading(true);
      try {
        const res = await LocalNotifications.requestPermissions();
        if (res.display === 'granted') {
          setPermission('granted');
          setIsSubscribed(true);
          localStorage.setItem(STORAGE_KEY, 'true');
          await scheduleNativeCheckinReminders();
        } else {
          setPermission('denied');
          setIsSubscribed(false);
          localStorage.setItem(STORAGE_KEY, 'false');
        }
      } catch (err) {
        console.warn('Native notification permission error:', err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isSupported) {
      alert('Push notifications are not supported in this browser.');
      return;
    }

    // On iOS Safari outside standalone mode, guide user
    if (isIOSDevice && !isStandaloneMode) {
      alert(
        'On iOS, notifications require adding DigestLog to your Home Screen.\n\nTap the Share button at the bottom of Safari, tap "Add to Home Screen", then open DigestLog from your home screen.'
      );
      return;
    }

    setLoading(true);

    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        setIsSubscribed(false);
        localStorage.setItem(STORAGE_KEY, 'false');
        setLoading(false);
        return;
      }

      const reg = await navigator.serviceWorker.ready;

      let vapidKey = DEFAULT_VAPID_KEY;
      try {
        const res = await fetch('/api/push?action=key');
        if (res.ok) {
          const data = await res.json();
          if (data.publicKey) {
            vapidKey = data.publicKey;
          }
        }
      } catch {}

      if (reg.pushManager) {
        try {
          let sub = await reg.pushManager.getSubscription();
          if (!sub) {
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidKey),
            });
          }

          if (sub) {
            await syncSubscriptionWithServer(sub);
          }
        } catch (mgrErr) {
          console.warn('pushManager.subscribe failed, local notifications active:', mgrErr);
        }
      }

      setIsSubscribed(true);
      localStorage.setItem(STORAGE_KEY, 'true');

      // Trigger immediate confirmation notification
      reg.showNotification('DigestLog Reminders Active', {
        body: 'Reminders for meal logs, morning baselines, and digestion check-ins are enabled!',
        icon: '/icons/icon-192.png',
        badge: '/favicon.svg',
      });
    } catch (err) {
      console.error('Push subscription failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribe = async () => {
    setLoading(true);
    try {
      if (Capacitor.isNativePlatform()) {
        try {
          await LocalNotifications.cancel({ notifications: [{ id: 101 }, { id: 102 }, { id: 103 }, { id: 104 }] });
        } catch {}
        setIsSubscribed(false);
        localStorage.setItem(STORAGE_KEY, 'false');
        return;
      }

      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        if (reg.pushManager) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            await fetch('/api/push', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'unsubscribe', endpoint: sub.endpoint }),
            }).catch(() => {});
            await sub.unsubscribe();
          }
        }
      }
      setIsSubscribed(false);
      localStorage.setItem(STORAGE_KEY, 'false');
    } catch (err) {
      console.error('Failed to unsubscribe from push notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSubscription = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  // Immediate local test notification
  const sendTestNotification = async (
    title = 'DigestLog Test Reminder',
    body = 'Notifications are working properly on your device!'
  ) => {
    try {
      if (Capacitor.isNativePlatform()) {
        const perm = await LocalNotifications.checkPermissions();
        if (perm.display !== 'granted') {
          const req = await LocalNotifications.requestPermissions();
          if (req.display !== 'granted') return false;
        }
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Math.random() * 100000),
              title,
              body,
              schedule: { at: new Date(Date.now() + 1000) },
            },
          ],
        });
        return true;
      }

      if ('Notification' in window && Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        setPermission(perm);
        if (perm !== 'granted') return false;
      }

      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        await reg.showNotification(title, {
          body,
          icon: '/icons/icon-192.png',
          badge: '/favicon.svg',
          vibrate: [150, 80, 150],
        } as any);
        return true;
      } else if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: '/icons/icon-192.png',
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to send test notification:', err);
      return false;
    }
  };

  // Real server-side push test through APNs / FCM (with optional delay to test when app is closed)
  const sendBackgroundTestPush = async (
    delaySeconds = 5
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      if (Capacitor.isNativePlatform()) {
        const perm = await LocalNotifications.checkPermissions();
        if (perm.display !== 'granted') {
          const req = await LocalNotifications.requestPermissions();
          if (req.display !== 'granted') {
            return { success: false, message: 'Notification permission not granted' };
          }
        }
        await LocalNotifications.schedule({
          notifications: [
            {
              id: Math.floor(Math.random() * 100000),
              title: 'DigestLog Lock Screen Alert',
              body: `Background delivery verified! Received with ${delaySeconds}s delay on your iPhone.`,
              schedule: { at: new Date(Date.now() + delaySeconds * 1000) },
            },
          ],
        });
        return {
          success: true,
          message: `Scheduled test alert in ${delaySeconds} seconds. Lock your phone now to test!`,
        };
      }
      let endpoint: string | undefined;
      let keys: any;
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.ready;
        if (reg.pushManager) {
          const sub = await reg.pushManager.getSubscription();
          if (sub) {
            endpoint = sub.endpoint;
            const json = sub.toJSON ? sub.toJSON() : undefined;
            keys = json?.keys;
          }
        }
      }

      const res = await fetch('/api/push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          test: true,
          delaySeconds,
          endpoint,
          keys,
          title: 'DigestLog Closed-App Alert',
          body:
            delaySeconds > 0
              ? 'Background delivery verified! Received from Apple/Google push servers while app was closed.'
              : 'DigestLog background push notifications are functioning properly.',
        }),
      });

      if (res.ok) {
        const data = await res.json();
        return { success: true, message: data.message };
      }
      const errData = await res.json().catch(() => ({}));
      return { success: false, message: errData.error || 'Server rejected push request' };
    } catch (err) {
      console.error('Failed to send background test push:', err);
      return { success: false, message: 'Network error connecting to push server' };
    }
  };

  const syncScheduleToServer = useCallback(async () => {
    try {
      if (!('serviceWorker' in navigator)) return;
      const reg = await navigator.serviceWorker.ready;
      if (!reg.pushManager) return;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await syncSubscriptionWithServer(sub);
      }
    } catch (e) {
      console.warn('Failed to sync reminder schedule to server:', e);
    }
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    isIOSDevice,
    isStandaloneMode,
    subscribe,
    unsubscribe,
    toggleSubscription,
    sendTestNotification,
    sendBackgroundTestPush,
    syncScheduleToServer,
  };
}
